import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import ContactPicker from '../components/ContactPicker';
import BarnPicker from '../components/BarnPicker';
import toast from 'react-hot-toast';

const DISCIPLINES = ['Dressage','Hunter','Jumper','Eventing','Western trail','Barrel racing','Roping','Reining','Endurance','Pleasure','Other'];
const GENDERS = [{ val: 'mare', label: 'Mare' }, { val: 'gelding', label: 'Gelding' }, { val: 'stallion', label: 'Stallion' }, { val: 'filly', label: 'Filly' }, { val: 'colt', label: 'Colt' }];
const FARRIER_INTERVALS = [{ val: 4, label: '4 weeks' }, { val: 6, label: '6 weeks' }, { val: 8, label: '8 weeks' }, { val: 10, label: '10 weeks' }, { val: 12, label: '12 weeks' }];

const EditHorsePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [insDoc, setInsDoc] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [form, setForm] = useState({
    name: '', breed: '', age: '', gender: '', color: '', markings: '', height: '', date_of_birth: '', sire: '', dam: '', date_of_birth: '', sire: '', dam: '', date_of_birth: '', sire: '', dam: '', blanket_size: '', blanket_color: '', sheet_size: '', sheet_color: '',
    discipline: '', registration_number: '', microchip: '',
    vet_name: '', vet_phone: '', farrier_name: '', farrier_phone: '',
    farrier_interval_weeks: 6, feed_notes: '', barn_name: '', barn_location: '',
    bio: '', privacy: 'public',
  });


  useEffect(() => {
    api.get('/documents').then(r => {
      const docs = Array.isArray(r.data) ? r.data : (r.data.documents || []);
      const d = docs.filter(x => x.document_type === 'insurance' && x.horse_id === id)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
      setInsDoc(d || null);
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    api.get(`/horses/${id}`).then(r => {
      const h = r.data;
      setForm({
        name: h.name || '',
        breed: h.breed || '',
        age: h.age || '',
        gender: h.gender || '',
        color: h.color || '',
        markings: h.markings || '',
        height: h.height || '',
        date_of_birth: h.date_of_birth || '',
        sire: h.sire || '',
        dam: h.dam || '',
        blanket_size: h.blanket_size || '',
        blanket_color: h.blanket_color || '',
        sheet_size: h.sheet_size || '',
        sheet_color: h.sheet_color || '',
        discipline: h.discipline || '',
        registration_number: h.registration_number || '',
        microchip: h.microchip || '',
        vet_name: h.vet_name || '',
        vet_phone: h.vet_phone || '',
        farrier_name: h.farrier_name || '',
        farrier_phone: h.farrier_phone || '',
        farrier_interval_weeks: h.farrier_interval_weeks || 6,
        feed_notes: h.feed_notes || '',
        barn_name: h.barn_name || '',
        barn_location: h.barn_location || '',
        bio: h.bio || '',
        privacy: h.privacy || 'public',
      });
      if (h.avatar_url) setPhotoPreview(h.avatar_url);
    }).catch(() => toast.error('Failed to load horse'))
    .finally(() => setLoading(false));
  }, [id]);

  const handleChange = useCallback((field) => (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const setVal = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
      if (photoFile) fd.append('avatar', photoFile);
      await api.put(`/horses/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Horse updated!');
      navigate(`/horses/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update horse');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 40 }}>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate(`/horses/${id}`)}
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20, padding: '4px 8px 4px 0' }}>‹</button>
          <h1 style={{ color: 'white', fontSize: 17, flex: 1 }}>Edit {form.name}</h1>
          <button onClick={handleSave} disabled={saving}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 13, borderRadius: 8, padding: '6px 14px', fontWeight: 500 }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div style={{ padding: '16px 12px' }}>

        {/* Photo */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); fileRef.current.click(); }}
            style={{ width: 90, height: 90, borderRadius: '50%', border: '2px dashed var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', cursor: 'pointer', overflow: 'hidden', background: 'var(--bg-secondary)' }}>
            {photoPreview
              ? <img src={photoPreview} alt="Horse" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 32 }}>🐴</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--cr-green)', cursor: 'pointer' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); fileRef.current.click(); }}>
            {photoPreview ? 'Change photo' : 'Add photo'}
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhoto} onClick={(e) => e.stopPropagation()} />
        </div>

        {/* Basic info */}
        <div className="card">
          <h3 style={{ marginBottom: 14 }}>Basic information</h3>
          <div className="form-group">
            <label className="form-label">Horse name *</label>
            <input className="form-input" type="text" value={form.name} onChange={handleChange('name')} autoComplete="off" />
          </div>
          <div className="form-group">
            <label className="form-label">Gender</label>
            <div className="chip-group">
              {GENDERS.map(g => (
                <div key={g.val} className={`chip ${form.gender === g.val ? 'selected' : ''}`} onClick={() => setVal('gender', g.val)}>{g.label}</div>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label className="form-label">Age</label>
              <input className="form-input" type="number" value={form.age} onChange={handleChange('age')} />
            </div>
            <div className="form-group">
              <label className="form-label">Breed</label>
              <input className="form-input" type="text" value={form.breed} onChange={handleChange('breed')} autoComplete="off" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Color</label>
            <input className="form-input" type="text" value={form.color} onChange={handleChange('color')} autoComplete="off" />
          </div>
                    <div className="form-group">
            <label className="form-label">Date of birth</label>
            <input type="date" className="form-input" value={form.date_of_birth || ''} onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Sire</label>
              <input className="form-input" value={form.sire || ''} onChange={e => setForm(f => ({ ...f, sire: e.target.value }))} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Dam</label>
              <input className="form-input" value={form.dam || ''} onChange={e => setForm(f => ({ ...f, dam: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Height (hands)</label>
            <input className="form-input" type="text" inputMode="decimal" placeholder="e.g. 15.2" value={form.height || ''} onChange={handleChange('height')} autoComplete="off" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Markings</label>
            <input className="form-input" type="text" value={form.markings} onChange={handleChange('markings')} autoComplete="off" />
          </div>
        </div>

        {/* Discipline */}
        <div className="card">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Discipline</label>
            <div className="chip-group">
              {DISCIPLINES.map(d => (
                <div key={d} className={`chip ${form.discipline === d ? 'selected' : ''}`} onClick={() => setVal('discipline', d)}>{d}</div>
              ))}
            </div>
          </div>
        </div>

        {/* Vet & farrier */}
        <div className="card">
          <h3 style={{ marginBottom: 14 }}>ID & registration</h3>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Registration number</label>
              <input className="form-input" type="text" placeholder="e.g. USEF 12345" value={form.registration_number} onChange={handleChange('registration_number')} autoComplete="off" />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Microchip</label>
              <input className="form-input" type="text" placeholder="15-digit chip number" value={form.microchip} onChange={handleChange('microchip')} autoComplete="off" />
            </div>
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 14 }}>Veterinarian</h3>
          <div className="form-group">
            <label className="form-label">Vet name</label>
            <ContactPicker contactType="vet" value={form.vet_name} onChange={handleChange('vet_name')} onPick={(c) => setForm(prev => ({ ...prev, vet_name: c.name, vet_phone: c.phone || prev.vet_phone }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Vet phone</label>
            <input className="form-input" type="tel" value={form.vet_phone} onChange={handleChange('vet_phone')} />
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 14 }}>Farrier</h3>
          <div className="form-group">
            <label className="form-label">Farrier name</label>
            <ContactPicker contactType="farrier" value={form.farrier_name} onChange={handleChange('farrier_name')} onPick={(c) => setForm(prev => ({ ...prev, farrier_name: c.name, farrier_phone: c.phone || prev.farrier_phone }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Farrier phone</label>
            <input className="form-input" type="tel" value={form.farrier_phone} onChange={handleChange('farrier_phone')} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Shoeing interval</label>
            <div className="chip-group">
              {FARRIER_INTERVALS.map(fi => (
                <div key={fi.val} className={`chip ${form.farrier_interval_weeks === fi.val ? 'selected' : ''}`} onClick={() => setVal('farrier_interval_weeks', fi.val)}>{fi.label}</div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 8 }}>Insurance</h3>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
            {insDoc ? ([insDoc.issued_by, insDoc.provider_phone, insDoc.policy_number && ('Policy ' + insDoc.policy_number)].filter(Boolean).join(' \u00b7 ') || 'On file') : 'None on file'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            💡 Insurance is managed from the Documents screen — upload or edit the insurance document there and it updates here and on the stall card automatically.
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 14 }}>Blanketing</h3>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Blanket size</label>
              <input className="form-input" placeholder="e.g. 78" value={form.blanket_size || ''} onChange={e => setForm(f => ({ ...f, blanket_size: e.target.value }))} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Blanket color</label>
              <input className="form-input" value={form.blanket_color || ''} onChange={e => setForm(f => ({ ...f, blanket_color: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Sheet size</label>
              <input className="form-input" value={form.sheet_size || ''} onChange={e => setForm(f => ({ ...f, sheet_size: e.target.value }))} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Sheet color</label>
              <input className="form-input" value={form.sheet_color || ''} onChange={e => setForm(f => ({ ...f, sheet_color: e.target.value }))} />
            </div>
          </div>
        </div>

        {/* Barn & feed */}
        <div className="card">
          <h3 style={{ marginBottom: 14 }}>Home barn</h3>
          <div className="form-group">
            <label className="form-label">Barn name</label>
            <BarnPicker value={form.barn_name} onChange={handleChange('barn_name')} onPick={(b) => setForm(prev => ({ ...prev, barn_name: b.name, barn_location: b.location || prev.barn_location }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Location</label>
            <input className="form-input" type="text" value={form.barn_location} onChange={handleChange('barn_location')} autoComplete="off" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Feed & supplement notes</label>
            <textarea className="form-input" value={form.feed_notes} onChange={handleChange('feed_notes')} rows={3} />
          </div>
        </div>

        {/* Bio & privacy */}
        <div className="card">
          <h3 style={{ marginBottom: 14 }}>Community profile</h3>
          <div className="form-group">
            <label className="form-label">Bio</label>
            <textarea className="form-input" value={form.bio} onChange={handleChange('bio')} rows={3} placeholder="Tell the community about your horse..." />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Privacy</label>
            {[{ val: 'public', label: 'Public', desc: 'Anyone can find and follow' }, { val: 'barn', label: 'Barn only', desc: 'Only your barn group can see' }, { val: 'private', label: 'Private', desc: 'Only you can see' }].map(p => (
              <label key={p.val} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}>
                <input type="radio" name="privacy" value={p.val} checked={form.privacy === p.val} onChange={() => setVal('privacy', p.val)} style={{ marginTop: 2, accentColor: 'var(--cr-green)' }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{p.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <button className="btn btn-primary btn-full btn-lg" onClick={handleSave} disabled={saving} style={{ marginTop: 8 }}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
        <button className="btn btn-secondary btn-full" onClick={() => navigate(`/horses/${id}`)} style={{ marginTop: 8 }}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default EditHorsePage;